import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from './LanguageSelector';
import { api } from '../lib/api';

export default function Layout({ children }) {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useTranslation();
  
  const { data } = useQuery({
    queryKey: ['auth'],
    queryFn: api.auth.me,
  });

  const logoutMutation = useMutation({
    mutationFn: api.auth.logout,
    onSuccess: () => {
      navigate('/login');
    },
  });

  const user = data?.user;

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'ADMIN':
        return 'bg-sonarges-red-100 text-sonarges-red-700 dark:bg-sonarges-red-900/30 dark:text-sonarges-red-400';
      case 'ORGANIZER':
        return 'bg-sonarges-green-100 text-sonarges-green-700 dark:bg-sonarges-green-900/30 dark:text-sonarges-green-400';
      default:
        return 'bg-sonarges-gray-100 text-sonarges-gray-700 dark:bg-sonarges-gray-700 dark:text-sonarges-gray-300';
    }
  };

  const canManageUsers = user?.global_role === 'ADMIN';

  return (
    <div className="min-h-screen bg-sonarges-gray-50 dark:bg-sonarges-gray-900 transition-colors duration-300">
      <nav className="bg-white dark:bg-sonarges-gray-800 shadow-lg border-b-4 border-sonarges-green-500 dark:border-sonarges-green-600 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-8 rtl:space-x-reverse">
              <Link to="/" className="flex items-center space-x-3 group">
                <img
                  src={isDark ? "/logo-sonarges-white.png" : "/logo-sonarges-color.png"}
                  alt="SONARGES"
                  className="h-12 w-auto object-contain transform transition-transform duration-200 group-hover:scale-105"
                />
              </Link>

              <div className="hidden md:flex space-x-1 rtl:space-x-reverse">
                <Link
                  to="/"
                  className="px-4 py-2 rounded-lg text-sonarges-gray-700 dark:text-sonarges-gray-300 hover:bg-sonarges-green-50 dark:hover:bg-sonarges-green-900/20 hover:text-sonarges-green-700 dark:hover:text-sonarges-green-400 font-medium transition-all duration-200"
                >
                  {t('nav.dashboard')}
                </Link>
                <Link
                  to="/instances"
                  className="px-4 py-2 rounded-lg text-sonarges-gray-700 dark:text-sonarges-gray-300 hover:bg-sonarges-green-50 dark:hover:bg-sonarges-green-900/20 hover:text-sonarges-green-700 dark:hover:text-sonarges-green-400 font-medium transition-all duration-200"
                >
                  {t('nav.instances')}
                </Link>
                {canManageUsers && (
                  <Link
                    to="/users"
                    className="px-4 py-2 rounded-lg text-sonarges-gray-700 dark:text-sonarges-gray-300 hover:bg-sonarges-green-50 dark:hover:bg-sonarges-green-900/20 hover:text-sonarges-green-700 dark:hover:text-sonarges-green-400 font-medium transition-all duration-200"
                  >
                    {t('nav.users')}
                  </Link>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <LanguageSelector />
              
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg bg-sonarges-gray-100 dark:bg-sonarges-gray-700 hover:bg-sonarges-gray-200 dark:hover:bg-sonarges-gray-600 transition-all duration-200"
                aria-label="Toggle theme"
              >
                {isDark ? (
                  <svg className="w-5 h-5 text-sonarges-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-sonarges-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {user && (
                <div className="flex items-center space-x-3 rtl:space-x-reverse">
                  <div className="text-right hidden sm:block rtl:text-left">
                    <p className="text-sm font-semibold text-sonarges-gray-900 dark:text-white">
                      {user.name}
                    </p>
                    <span className={`inline-block px-2 py-0.5 text-xs font-semibold rounded ${getRoleBadgeColor(user.global_role)}`}>
                      {t(`users.${user.global_role}`)}
                    </span>
                  </div>
                  <button
                    onClick={() => logoutMutation.mutate()}
                    className="px-4 py-2 bg-sonarges-red-600 hover:bg-sonarges-red-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
                  >
                    {t('auth.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
}
